import { Box, TextField, Alert } from '@mui/material'
import { useState } from 'react'
import { Button } from '@/components/common'
import { useFormValidation } from '@/hooks/useFormValidation'

/**
 * Contact Form Component
 * Form with Netlify Forms integration for email notifications
 */
export const ContactForm = () => {
  const { formData, errors, touched, handleChange, handleBlur, validateAll, isValid, reset } = useFormValidation()
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateAll()) {
      return
    }

    setSubmitting(true)
    setSubmitStatus(null)

    try {
      // Encode form data for Netlify Forms
      const encode = (data: Record<string, string>) => {
        return Object.keys(data)
          .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
          .join('&')
      }

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact',
          'bot-field': '',
          ...formData,
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
        reset()
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000)
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      {submitStatus === 'success' && (
        <Alert severity="success" onClose={() => setSubmitStatus(null)}>
          Thank you for your message! We'll get back to you shortly.
        </Alert>
      )}

      {submitStatus === 'error' && (
        <Alert severity="error" onClose={() => setSubmitStatus(null)}>
          Something went wrong. Please try again or contact us directly at info@lerusystems.com.
        </Alert>
      )}

      {/* Name Field */}
      <TextField
        label="Name"
        name="name"
        required
        fullWidth
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        onBlur={() => handleBlur('name')}
        error={touched.name && !!errors.name}
        helperText={touched.name && errors.name}
      />

      {/* Company Field */}
      <TextField
        label="Company Name"
        name="company"
        fullWidth
        value={formData.company}
        onChange={(e) => handleChange('company', e.target.value)}
        onBlur={() => handleBlur('company')}
      />

      {/* Email Field */}
      <TextField
        label="Email"
        name="email"
        type="email"
        required
        fullWidth
        value={formData.email}
        onChange={(e) => handleChange('email', e.target.value)}
        onBlur={() => handleBlur('email')}
        error={touched.email && !!errors.email}
        helperText={touched.email && errors.email}
      />

      {/* Phone Field */}
      <TextField
        label="Phone"
        name="phone"
        type="tel"
        fullWidth
        value={formData.phone}
        onChange={(e) => handleChange('phone', e.target.value)}
        onBlur={() => handleBlur('phone')}
        error={touched.phone && !!errors.phone}
        helperText={touched.phone && errors.phone}
      />

      {/* Message Field */}
      <TextField
        label="Message"
        name="message"
        required
        fullWidth
        multiline
        rows={4}
        value={formData.message}
        onChange={(e) => handleChange('message', e.target.value)}
        onBlur={() => handleBlur('message')}
        error={touched.message && !!errors.message}
        helperText={touched.message && errors.message}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="large"
        fullWidth
        disabled={!isValid || submitting}
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
    </Box>
  )
}
